import {styled} from "@mui/material/styles"
import {Box, BoxProps, Skeleton} from "@mui/material"
import Grid from "@mui/material/Grid"

import {colors, gradients} from "@indebted/theme/colors"
import {HorizontalContentPadding, Page} from "@indebted/components/Page"
import {AppNewThemeProvider} from "@indebted/theme/AppTheme"

function SecureCodeSkeleton() {
	return (
		<AppNewThemeProvider>
			<MainContainer data-testid="secure-code-skeleton">
				<HeroWrapper>
					<HeroHeader>
						<Box>
							<Skeleton
								variant="text"
								animation="wave"
								width={70}
								height={20}
								sx={{bgcolor: "rgba(255, 255, 255, 0.1)", marginBottom: "0.5rem"}}
							/>
							<Skeleton
								variant="text"
								animation="wave"
								width={170}
								height={20}
								sx={{bgcolor: "rgba(255, 255, 255, 0.1)"}}
							/>
						</Box>
						<Skeleton
							variant="rectangular"
							animation="wave"
							width={41}
							height={41}
							sx={{bgcolor: "rgba(255, 255, 255, 0.1)", borderRadius: 1}}
						/>
					</HeroHeader>

					<BalanceText>
						<Skeleton
							variant="text"
							animation="wave"
							width={180}
							height={20}
							sx={{
								bgcolor: "rgba(255, 255, 255, 0.1)",
								marginBottom: "0.5rem",
								mx: "auto",
							}}
						/>
						<Skeleton
							variant="text"
							animation="wave"
							width={140}
							height={44}
							sx={{
								bgcolor: "rgba(255, 255, 255, 0.1)",
								marginBottom: "1rem",
								mx: "auto",
							}}
						/>
						<Divider />
						<Skeleton
							variant="text"
							animation="wave"
							width={220}
							height={20}
							sx={{
								bgcolor: "rgba(255, 255, 255, 0.1)",
								mx: "auto",
							}}
						/>
					</BalanceText>
				</HeroWrapper>

				<Container>
					{/* Limited Time Offer Section Skeleton */}
					<Box
						sx={{
							width: "100%",
							padding: "1.5rem 0rem 0px 0rem",
							"& section": {alignContent: "center"},
						}}
					>
						<Skeleton
							variant="text"
							animation="wave"
							width={150}
							height={30}
							sx={{
								marginBottom: "0.5rem",
								mx: "auto",
							}}
						/>
						<Skeleton
							variant="text"
							animation="wave"
							width={160}
							height={20}
							sx={{
								marginBottom: "1.25rem",
								mx: "auto",
							}}
						/>

						<Grid container spacing={4} sx={{paddingTop: "20px"}}>
							<Grid size={{xs: 12, md: 6}}>
								<SettlementOfferSkeleton />
							</Grid>
							<Grid size={{xs: 12, md: 6}}>
								<SettlementOfferSkeleton />
							</Grid>
						</Grid>
					</Box>

					{/* Payment Options Section Skeleton */}
					<PaymentBox>
						<Skeleton
							variant="text"
							animation="wave"
							width={150}
							height={30}
							sx={{
								marginBottom: "0.5rem",
								mx: "auto",
							}}
						/>
						<Skeleton
							variant="text"
							animation="wave"
							width={160}
							height={20}
							sx={{
								marginBottom: "1.25rem",
								mx: "auto",
							}}
						/>

						<Grid container spacing={4} sx={{paddingTop: "20px", margin: "0px"}}>
							<Grid size={{xs: 12}} sx={{paddingRight: "0px", paddingLeft: "0px", margin: "0px"}}>
								<PaymentOptionSkeleton />
							</Grid>
							<Grid size={{xs: 12}} sx={{paddingRight: "0px", paddingLeft: "0px", margin: "0px"}}>
								<PaymentOptionSkeleton />
							</Grid>
							<Grid size={{xs: 12}} sx={{paddingRight: "0px", paddingLeft: "0px", margin: "0px"}}>
								<PaymentOptionSkeleton />
							</Grid>
						</Grid>
					</PaymentBox>
				</Container>

				<Footer>
					<Skeleton
						variant="rectangular"
						animation="wave"
						width={120}
						height={40}
						sx={{marginBottom: "1rem", borderRadius: 1}}
					/>

					<Box mt={2}>
						<Skeleton
							variant="text"
							animation="wave"
							width="100%"
							height={20}
							sx={{marginBottom: "0.5rem"}}
						/>
						<Skeleton
							variant="text"
							animation="wave"
							width="100%"
							height={20}
							sx={{marginBottom: "0.5rem"}}
						/>
						<Skeleton
							variant="text"
							animation="wave"
							width="85%"
							height={20}
							sx={{marginBottom: "0.5rem"}}
						/>
						<Box sx={{display: "flex", gap: "0.5rem", paddingTop: "1.5rem", alignItems: "center"}}>
							<Skeleton
								variant="rectangular"
								animation="wave"
								width={24}
								height={24}
								sx={{borderRadius: 1}}
							/>
							<Skeleton variant="text" animation="wave" width="60%" height={20} />
						</Box>
					</Box>
				</Footer>
			</MainContainer>
		</AppNewThemeProvider>
	)
}

// Settlement Offer Skeleton Component
function SettlementOfferSkeleton() {
	return (
		<DiscountBox>
			<DiscountHeader>
				<Skeleton
					variant="text"
					animation="wave"
					width={120}
					height={20}
					sx={{
						bgcolor: "rgba(255, 255, 255, 0.1)",
						marginBottom: "0.5rem",
					}}
				/>
				<Skeleton
					variant="text"
					animation="wave"
					width={100}
					height={44}
					sx={{
						bgcolor: "rgba(255, 255, 255, 0.1)",
					}}
				/>
			</DiscountHeader>
			<DiscountBody>
				<DiscountBodyContent>
					<Skeleton variant="text" animation="wave" width="50%" height={24} sx={{marginBottom: "0.5rem"}} />
					<Skeleton variant="text" animation="wave" width="70%" height={44} sx={{marginBottom: "0.5rem"}} />
					<Skeleton variant="text" animation="wave" width="100%" height={20} sx={{marginBottom: "0.5rem"}} />
					<Skeleton variant="text" animation="wave" width="85%" height={20} />
				</DiscountBodyContent>
				<Skeleton variant="rectangular" animation="wave" width="100%" height={46} sx={{borderRadius: 1}} />
			</DiscountBody>
		</DiscountBox>
	)
}

// Payment Option Skeleton Component
function PaymentOptionSkeleton() {
	return (
		<OptionBox component="section" alignContent="center">
			<TextGroup>
				<Skeleton variant="text" animation="wave" width="100%" height={20} sx={{marginBottom: "0.5rem"}} />
				<Skeleton variant="text" animation="wave" width="100%" height={20} />
			</TextGroup>
		</OptionBox>
	)
}

// Reused styled components from OutstandingBalance component
const MainContainer = styled("div")`
	background-color: ${colors.purpleIllusion["100"]};
	font-family: "Manrope", sans-serif;
	margin: 0 auto;
	padding: 0rem;
	flex-direction: column;
	min-height: 100vh;
	display: flex;
`

const Container = styled(Page)`
	background-color: ${colors.purpleIllusion["100"]};
	font-family: "Manrope", sans-serif;
	margin: 0 auto;
	padding: 0rem;
	flex: 1;
	width: 100%;
	${HorizontalContentPadding}
`

const Footer = styled(Box)`
	background-color: ${colors.purpleIllusion["100"]};
	padding: 3rem 0rem;
	padding-top: 2rem;
	font-size: 0.9rem;
	${HorizontalContentPadding}
`

const HeroWrapper = styled(Box)`
	background: ${gradients["brand-gradient-4-90deg"]};
	color: white;
	padding: 2.5rem;
	${HorizontalContentPadding}
`

const HeroHeader = styled(Box)`
	display: flex;
	justify-content: space-between;
	align-items: center;
`

const BalanceText = styled(Box)`
	text-align: center;
	margin-top: 2rem;
`

const Divider = styled("hr")`
	margin: 1rem auto;
	border: 0;
	border-top: 1px solid rgba(255, 255, 255, 0.2);
	width: 60%;
`

const PaymentBox = styled(Box)`
	width: 100%;
	padding: 1rem 0;

	/* >360px */
	@media (min-width: 360px) {
		padding: 2rem 0;
	}
`

const DiscountBox = styled(Box)(({theme}) => ({
	display: "flex",
	flexDirection: "column",
	background: theme.palette.common.white,
	borderRadius: 16,
	overflow: "hidden",
	padding: 0,
	boxShadow: "0 4px 14px 2px rgba(54, 59, 100, 0.10)",
	width: "100%",
}))

const DiscountHeader = styled(Box)(({theme}) => ({
	background: colors.endlessGalaxy["900"],
	color: theme.palette.common.white,
	textAlign: "left",
	/* top | left and right | bottom */
	padding: "1rem 1rem 0.25rem",
	[theme.breakpoints.up(360)]: {
		padding: "1.25rem 1rem 0.25rem",
	},
	[theme.breakpoints.up(411)]: {
		padding: "1.25rem 1rem 0.5rem",
	},
	[theme.breakpoints.up(600)]: {
		padding: "1.25rem 1.5rem 0.5rem",
	},
	[theme.breakpoints.up(900)]: {
		padding: "1.25rem 2rem 0.5rem",
	},
}))

const DiscountBody = styled(Box)(({theme}) => ({
	/* top | left and right | bottom */
	padding: "1rem",
	[theme.breakpoints.up(360)]: {
		padding: "1rem 1rem 1.25rem",
	},
	[theme.breakpoints.up(411)]: {
		padding: "1.5rem 1rem 1.25rem",
	},
	[theme.breakpoints.up(600)]: {
		padding: "1.5rem 1.5rem 1.25rem",
	},
	[theme.breakpoints.up(900)]: {
		padding: "1.5rem 2rem 1.5rem",
	},
}))

const DiscountBodyContent = styled(Box)(({theme}) => ({
	paddingBottom: "1rem",
	[theme.breakpoints.up(411)]: {
		paddingBottom: "1.5rem",
	},
}))

const OptionBox = styled(Box)<BoxProps>(({theme}) => ({
	display: "flex",
	width: "100%",
	justifyContent: "space-between",
	alignItems: "center",
	backgroundColor: theme.palette.common.white,
	boxShadow: "0 4px 14px 2px rgba(54, 59, 100, 0.10)",
	borderRadius: 8,
	padding: "1.5rem",
}))

const TextGroup = styled(Box)`
	display: flex;
	flex-direction: column;
	width: 100%;
`

export {SecureCodeSkeleton}
